import React, { useContext, useMemo, useRef } from "react"
import { get, useFormContext, useFormState, useWatch } from "react-hook-form"

import classNames from "classnames"

import { useAutoFocus } from "shared/lib/useAutoFocus"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { useAutosizeTextArea } from "shared/ui/input/text/hooks/useAutosizeTextArea"

import { Input as DefaultInput, InputContext } from "../Input"
import { InputFocus } from "../InputFocus"
import { useInputDescriptionIDs } from "../accessibility/useInputAriaDescription"
import { TextInputRootProps } from "./TextInput"
import { TextInputContext } from "./TextInputContext"
import { ReadonlyText } from "./components/ReadonlyText"
import { TextInputCounter } from "./components/TextInputCounter"
import { TextInputErrorMessage } from "./components/TextInputErrorMessage"

const Root = React.memo(
  React.forwardRef(
    (
      { maxLength, minLength, className, ...props }: TextInputRootProps,
      ref?: React.ForwardedRef<HTMLDivElement>
    ) => {
      const context = useMemo(
        () => ({ maxLength, minLength }),
        [maxLength, minLength]
      )
      return (
        <TextInputContext.Provider value={context}>
          <DefaultInput.Root
            {...props}
            className={classNames("textarea-input", className)}
            ref={ref}
          />
        </TextInputContext.Provider>
      )
    }
  )
)

const InputBase = React.memo(
  React.forwardRef(
    (
      { className, ...rest }: React.HTMLProps<HTMLTextAreaElement>,
      externalRef: React.ForwardedRef<HTMLTextAreaElement>
    ) => {
      const { inputRef } = useContext(InputContext)
      const internalRef = useRef<HTMLTextAreaElement>(null)
      const containerRef = useRef<HTMLDivElement>(null)
      const { id, readonly, autoFocus, label } = useContext(InputContext)
      const { minLength, maxLength } = useContext(TextInputContext)
      const { ariaDescribedBy } = useInputDescriptionIDs({
        additionalDescriptions: rest["aria-describedby"]
      })

      useAutoFocus({ autoFocus, ref: internalRef })

      const ref = useMergedRefs(
        externalRef,
        internalRef,
        inputRef as React.RefObject<HTMLTextAreaElement>
      )

      useAutosizeTextArea({ ref: internalRef, containerRef })

      if (readonly) {
        return null
      }

      return (
        <div className="textarea-resize-container" ref={containerRef}>
          <textarea
            {...rest}
            minLength={minLength}
            maxLength={maxLength}
            id={id}
            className={classNames("textarea", className)}
            aria-label={label}
            aria-describedby={ariaDescribedBy}
            ref={ref}
          />
        </div>
      )
    }
  )
)

const HookFormInput = React.memo(
  React.forwardRef(
    (
      { className, ...rest }: React.HTMLProps<HTMLTextAreaElement>,
      externalRef: React.ForwardedRef<HTMLTextAreaElement>
    ) => {
      const { inputRef } = useContext(InputContext)
      const { name, readonly, required, disabled } = useContext(InputContext)
      const { minLength, maxLength } = useContext(TextInputContext)
      const { register } = useFormContext()
      const { errors } = useFormState({ name })
      const error = get(errors, name)
      useWatch({ name })

      const registerProps = register(name, {
        required,
        disabled,
        minLength,
        maxLength
      })

      const ref = useMergedRefs(
        externalRef,
        registerProps.ref,
        inputRef as React.RefObject<HTMLTextAreaElement>
      )

      if (readonly) {
        return null
      }

      return (
        <InputBase
          className={classNames({ error: !!error }, className)}
          {...registerProps}
          {...rest}
          ref={ref}
        />
      )
    }
  )
)

const Input = React.memo(
  React.forwardRef(
    (
      props: React.HTMLProps<HTMLTextAreaElement>,
      externalRef: React.ForwardedRef<HTMLTextAreaElement>
    ) => {
      const { name } = useContext(InputContext)

      if (name) {
        return <HookFormInput {...props} ref={externalRef} />
      }
      return <InputBase {...props} ref={externalRef} />
    }
  )
)

const Textarea = {
  ...DefaultInput,
  Root,
  Focus: InputFocus,
  Input,
  ErrorMessage: TextInputErrorMessage,
  Readonly: ReadonlyText,
  Counter: TextInputCounter
}

export { Textarea }
